/**
 * Route-level permission guard.
 *
 * A whole page the role cannot reach should say so rather than render an empty
 * shell, because the visitor got here by following a link or typing a URL and
 * deserves an answer. Reaching this screen is not an error — a bookmark kept
 * after a role change lands here — so it is written as an explanation rather
 * than a failure.
 *
 * As with every check in this client, it decides nothing: the endpoints behind
 * these pages are gated by `role_middleware` server-side.
 */
import type { ReactNode } from 'react'
import type { Permission } from '@accounting/shared'
import { useSession } from '../lib/session'
import { Forbidden } from '../routes/Forbidden'

export function RequirePermissionRoute({
  permission,
  children,
}: {
  permission: Permission | readonly Permission[]
  children: ReactNode
}) {
  const { may } = useSession()
  const required = Array.isArray(permission) ? permission : [permission as Permission]

  return <>{required.some((p) => may(p)) ? children : <Forbidden />}</>
}
